import { useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { FlowSchedule } from "../hooks/use-workflow-schedules"
import type { PlanUpgradePayload } from "@/api/fair-use"

import { Clock, Trash2 } from "@/components/icons"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Switch } from "@/components/ui/switch"
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip"
import { PlanUpgradeNotice } from "@/features/fair-use/components/plan-upgrade-notice"
import { extractPlanUpgradeFromError } from "@/features/fair-use/fair-use-plan-utils"
import { FlowPluginConsent } from "@/features/plugins/flow-plugin-picker"
import { pluginErrorMessage } from "@/features/plugins/plugin-utils"

import {
  useDeleteWorkflowSchedule,
  useUpsertWorkflowSchedule,
  useWorkflowSchedules,
} from "../hooks/use-workflow-schedules"
import {
  buildScheduleCreateInput,
  buildScheduleToggleInput,
  describeSchedule,
  getScheduleValidationError,
  scheduleActionError,
  scheduleConsentSettings,
  scheduleCreateConsentSettings,
  type ScheduleFrequency,
} from "../utils/workflow-schedule-utils"

/** Tile/toolbar button that opens the schedule dialog for a flow. */
export function ScheduleButton({
  label,
  workflowId,
  workflowTitle,
}: {
  label: string
  workflowId?: string
  workflowTitle?: string
}) {
  const [open, setOpen] = useState(false)

  if (!workflowId) return null

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex" role="presentation" onClick={(e) => e.stopPropagation()}>
            <Button
              aria-label={label}
              className="h-7 w-7 p-0"
              size="sm"
              title={label}
              variant="outline"
              onClick={(e) => {
                e.stopPropagation()
                setOpen(true)
              }}
            >
              <Clock className="h-3.5 w-3.5" />
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{label}</TooltipContent>
      </Tooltip>
      {/* Stop dialog clicks from bubbling (via the React tree, even when
          portaled) to the tile card's navigate-onClick. */}
      <div role="presentation" onClick={(e) => e.stopPropagation()}>
        <WorkflowScheduleDialog
          open={open}
          workflowId={workflowId}
          workflowTitle={workflowTitle}
          onOpenChange={setOpen}
        />
      </div>
    </>
  )
}

type WorkflowScheduleDialogProps = {
  workflowId: string
  workflowTitle?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

const INTERVAL_OPTIONS: { label: string; minutes: number }[] = [
  { label: "Every 15 minutes", minutes: 15 },
  { label: "Every 30 minutes", minutes: 30 },
  { label: "Every hour", minutes: 60 },
  { label: "Every 2 hours", minutes: 120 },
  { label: "Every 6 hours", minutes: 360 },
]

const WEEKDAY_OPTIONS: { label: string; value: string }[] = [
  { label: "Monday", value: "1" },
  { label: "Tuesday", value: "2" },
  { label: "Wednesday", value: "3" },
  { label: "Thursday", value: "4" },
  { label: "Friday", value: "5" },
  { label: "Saturday", value: "6" },
  { label: "Sunday", value: "0" },
]

export function WorkflowScheduleDialog({
  workflowId,
  workflowTitle,
  open,
  onOpenChange,
}: WorkflowScheduleDialogProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "workflowLanding" })
  const { t: tPlugins } = useTranslation()
  const { data: schedules = [], isLoading } = useWorkflowSchedules(open ? workflowId : undefined)
  const upsert = useUpsertWorkflowSchedule(workflowId)
  const remove = useDeleteWorkflowSchedule(workflowId)
  const [actionError, setActionError] = useState<string | null>(null)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  const [frequency, setFrequency] = useState<ScheduleFrequency>("daily")
  const [intervalMinutes, setIntervalMinutes] = useState(60)
  const [time, setTime] = useState("08:00")
  const [weekday, setWeekday] = useState("1")
  const [cron, setCron] = useState("0 8 * * *")
  const [timezone, setTimezone] = useState(
    Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"
  )
  const [defaultPrompt, setDefaultPrompt] = useState("")
  const [notifyEmail, setNotifyEmail] = useState("")
  // The plan's scheduled-flow entitlement answers this POST with a structured
  // 400 (`plan_upgrade_required`, routes/explorer.py `_scheduled_flow_cap_error`).
  // It is a policy answer, not a failure, so it stays on the dialog next to the
  // schedules it is talking about instead of flashing past in a toast.
  const [planUpgrade, setPlanUpgrade] = useState<PlanUpgradePayload | null>(null)

  const createSettings = scheduleCreateConsentSettings(defaultPrompt, notifyEmail)

  const describeActionError = (error: unknown, fallback: string) => {
    const code = scheduleActionError(error)
    setActionError(code)
    // Schedule failures always carry the stable code so support can name them.
    return pluginErrorMessage(tPlugins, code, { fallback, alwaysAppendCode: true })
  }

  const handleCreate = async () => {
    const form = { frequency, intervalMinutes, time, weekday, cron }
    const validationError = getScheduleValidationError(form)
    if (validationError) {
      toast.error(validationError)
      return
    }
    setPlanUpgrade(null)
    const payload = buildScheduleCreateInput(form, {
      timezone,
      defaultPrompt,
      notifyEmail,
    })
    try {
      setActionError(null)
      await upsert.mutateAsync(payload)
      toast.success(t("schedule.created", "Schedule created"))
      setDefaultPrompt("")
      setNotifyEmail("")
    } catch (error) {
      const refusal = extractPlanUpgradeFromError(error)
      if (refusal) {
        setPlanUpgrade(refusal)
        return
      }
      toast.error(
        describeActionError(error, t("schedule.createFailed", "Could not create schedule"))
      )
    }
  }

  const handleToggle = async (schedule: FlowSchedule, enabled: boolean) => {
    try {
      setActionError(null)
      await upsert.mutateAsync(buildScheduleToggleInput(schedule, enabled))
    } catch (error) {
      toast.error(
        describeActionError(error, t("schedule.updateFailed", "Could not update schedule"))
      )
    }
  }

  const handleDelete = async (id: string) => {
    try {
      setActionError(null)
      await remove.mutateAsync(id)
      // Removing a schedule frees the slot the notice was talking about.
      setPlanUpgrade(null)
      setPendingDeleteId(null)
      toast.success(t("schedule.deleted", "Schedule removed"))
    } catch (error) {
      toast.error(
        describeActionError(error, t("schedule.deleteFailed", "Could not remove schedule"))
      )
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Clock className="size-4" />
            {t("schedule.title", "Schedule flow")}
            {workflowTitle ? ` — ${workflowTitle}` : ""}
          </DialogTitle>
          <DialogDescription>
            {t("schedule.description", "Run this flow automatically on a recurring schedule.")}
          </DialogDescription>
        </DialogHeader>

        <div className="grid max-h-[70vh] gap-4 overflow-y-auto">
          {/* Existing schedules */}
          <div className="grid gap-2">
            <Label className="text-xs font-medium">
              {t("schedule.existing", "Active schedules")}
            </Label>
            {isLoading && (
              <p className="text-muted-foreground text-xs">{t("schedule.loading", "Loading…")}</p>
            )}
            {!isLoading && schedules.length === 0 && (
              <p className="text-muted-foreground text-xs">
                {t("schedule.none", "No schedules yet.")}
              </p>
            )}
            {!isLoading && schedules.length > 0 && (
              <ul className="grid gap-2">
                {schedules.map((s) => (
                  <li key={s.id} className="grid gap-2 rounded-md border p-2 text-xs">
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="truncate font-medium">{describeSchedule(s)}</div>
                        {s.blocked_reason && (
                          <p className="text-warning-foreground break-words" role="alert">
                            {t("schedule.identityPaused")}
                          </p>
                        )}
                        {s.default_prompt ? (
                          <div className="text-muted-foreground truncate">{s.default_prompt}</div>
                        ) : null}
                        <div className="text-muted-foreground truncate">
                          {s.timezone}
                          {s.next_run ? ` • next ${new Date(s.next_run).toLocaleString()}` : ""}
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-2">
                        <Switch
                          checked={s.enabled}
                          className="shrink-0"
                          disabled={upsert.isPending}
                          onCheckedChange={(v) => void handleToggle(s, v)}
                        />
                        <Button
                          aria-label={t("schedule.delete", "Delete schedule")}
                          className="size-7 shrink-0 p-0"
                          size="sm"
                          variant="ghost"
                          onClick={() => setPendingDeleteId(s.id)}
                        >
                          <Trash2 className="text-destructive size-3.5" />
                        </Button>
                      </div>
                    </div>
                    <div data-testid={`schedule-consent-${s.id}`}>
                      <FlowPluginConsent
                        flowId={workflowId}
                        settings={scheduleConsentSettings(s)}
                        showAccount={false}
                      />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {planUpgrade && (
            <PlanUpgradeNotice countScope="total" payload={planUpgrade} showSeePlan={false} />
          )}

          {/* New schedule */}
          <div className="grid gap-3 border-t pt-3">
            <div className="grid gap-1.5">
              <Label className="text-xs" htmlFor="schedule-frequency">
                {t("schedule.repeat", "Repeat")}
              </Label>
              <Select
                value={frequency}
                onValueChange={(value) => setFrequency(value as ScheduleFrequency)}
              >
                <SelectTrigger id="schedule-frequency">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="interval">
                    {t("schedule.interval", "Every few minutes")}
                  </SelectItem>
                  <SelectItem value="daily">{t("schedule.daily", "Daily")}</SelectItem>
                  <SelectItem value="weekly">{t("schedule.weekly", "Weekly")}</SelectItem>
                  <SelectItem value="advanced">
                    {t("schedule.advanced", "Advanced cron")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            {frequency === "interval" && (
              <div className="grid gap-1.5">
                <Label className="text-xs" htmlFor="schedule-interval">
                  {t("schedule.intervalLabel", "Interval")}
                </Label>
                <Select
                  value={String(intervalMinutes)}
                  onValueChange={(value) => setIntervalMinutes(Number(value))}
                >
                  <SelectTrigger id="schedule-interval">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {INTERVAL_OPTIONS.map((option) => (
                      <SelectItem key={option.minutes} value={String(option.minutes)}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {(frequency === "daily" || frequency === "weekly") && (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {frequency === "weekly" && (
                  <div className="grid gap-1.5">
                    <Label className="text-xs" htmlFor="schedule-weekday">
                      {t("schedule.weekday", "Day")}
                    </Label>
                    <Select value={weekday} onValueChange={setWeekday}>
                      <SelectTrigger id="schedule-weekday">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {WEEKDAY_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="grid gap-1.5">
                  <Label className="text-xs" htmlFor="schedule-time">
                    {t("schedule.time", "Time")}
                  </Label>
                  <Input
                    className="text-xs"
                    id="schedule-time"
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                  />
                </div>
              </div>
            )}
            {frequency === "advanced" && (
              <div className="grid gap-1.5">
                <Label className="text-xs" htmlFor="schedule-cron">
                  {t("schedule.cron", "Cron expression")}
                </Label>
                <Input
                  className="font-mono text-xs"
                  id="schedule-cron"
                  placeholder="0 8 * * *"
                  value={cron}
                  onChange={(e) => setCron(e.target.value)}
                />
              </div>
            )}
            <div className="grid gap-1.5">
              <Label className="text-xs" htmlFor="schedule-tz">
                {t("schedule.timezone", "Timezone")}
              </Label>
              <Input
                className="text-xs"
                id="schedule-tz"
                value={timezone}
                onChange={(e) => setTimezone(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs" htmlFor="schedule-prompt">
                {t("schedule.prompt", "Default input (optional)")}
              </Label>
              <Input
                className="text-xs"
                id="schedule-prompt"
                placeholder={t("schedule.promptPlaceholder", "Prompt passed to the flow")}
                value={defaultPrompt}
                onChange={(e) => setDefaultPrompt(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label className="text-xs" htmlFor="schedule-email">
                {t("schedule.notifyEmail", "Email the result to (optional)")}
              </Label>
              <Input
                className="text-xs"
                id="schedule-email"
                placeholder="you@company.com"
                type="email"
                value={notifyEmail}
                onChange={(e) => setNotifyEmail(e.target.value)}
              />
            </div>
          </div>
        </div>

        {actionError && (
          <p className="text-destructive text-xs" role="alert">
            {pluginErrorMessage(tPlugins, actionError, { alwaysAppendCode: true })}
          </p>
        )}
        <div data-testid="schedule-create-consent">
          <FlowPluginConsent flowId={workflowId} settings={createSettings} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("schedule.close", "Close")}
          </Button>
          <Button disabled={upsert.isPending} onClick={() => void handleCreate()}>
            {t("schedule.add", "Add schedule")}
          </Button>
        </DialogFooter>
      </DialogContent>

      <AlertDialog
        open={pendingDeleteId !== null}
        onOpenChange={(next) => {
          if (!next) setPendingDeleteId(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t("schedule.confirmDeleteTitle", "Delete schedule?")}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                "schedule.confirmDeleteDescription",
                "This schedule stops running and cannot be restored."
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>
              {t("schedule.cancel", "Cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={remove.isPending}
              onClick={(event) => {
                // Keep the dialog mounted until the mutation settles.
                event.preventDefault()
                if (pendingDeleteId) void handleDelete(pendingDeleteId)
              }}
            >
              {t("schedule.confirmDelete", "Delete")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Dialog>
  )
}
